import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, timer } from 'rxjs';
import { catchError, takeUntil, switchMap, tap } from 'rxjs/operators';
import { BaseRequestService } from '@outlaw/core';

import * as UpChunk from '@mux/upchunk';

export type VideoUploadStatus = 'idle' | 'uploading' | 'processing' | 'ready' | 'error';

export interface MuxUploadTicket {
  upload_id: string;
  video_id: number;
  upload_url: string;
}

export interface MuxVideoStatusResponse {
  status: 'waiting' | 'asset_created' | 'ready' | 'errored';
  video_url: string;
  asset_id?: string;
  id?: string;
}

@Injectable({ providedIn: 'root' })
export class VideoUploadService {
  #http = inject(HttpClient);
  #baseRequest = inject(BaseRequestService);

  status$ = new BehaviorSubject<VideoUploadStatus>('idle');
  progress$ = new BehaviorSubject<number>(0);
  streamUrl$ = new BehaviorSubject<string | null>(null);

  #uploadInstance: any = null;
  #stopPolling$ = new Subject<void>();
  #uploadId = '';
  #videoId = 0;

  upload(file: File): void {
    this.cancel();
    this.status$.next('uploading');
    this.progress$.next(0);

    this.getUploadTicket(file).pipe(
      takeUntil(this.#stopPolling$)
    ).subscribe({
      next: (ticket) => {
        this.#uploadId = ticket.upload_id;
        this.#videoId = ticket.video_id;
        this.startChunkedUpload(file, ticket);
      },
      error: () => {
        this.status$.next('error');
      }
    });
  }

  cancel(): void {
    if (this.#uploadInstance) {
      this.#uploadInstance.abort();
      this.#uploadInstance = null;
    }
    this.#stopPolling$.next();
    this.reset();
  }

  reset(): void {
    this.status$.next('idle');
    this.progress$.next(0);
    this.streamUrl$.next(null);
    this.#uploadId = '';
    this.#videoId = 0;
  }

  private getUploadTicket(file: File): Observable<MuxUploadTicket> {
    return this.#http.post<MuxUploadTicket>(
      this.#baseRequest.prepareUrl(['/api/videos']),
      { file_name: file.name, file_size: file.size, relation: 'video' }
    );
  }

  private startChunkedUpload(file: File, ticket: MuxUploadTicket): void {
    this.#uploadInstance = UpChunk.createUpload({
      endpoint: ticket.upload_url,
      file,
      chunkSize: 5120, // 5 MB
    });

    this.#uploadInstance.on('progress', (event: any) => {
      this.progress$.next(Math.round(event.detail));
    });

    this.#uploadInstance.on('success', () => {
      this.status$.next('processing');
      this.startStatusPolling();
    });

    this.#uploadInstance.on('error', () => {
      this.status$.next('error');
    });
  }

  private startStatusPolling(): void {
    const maxAttempts = 30;
    let attempts = 0;
    let consecutiveErrors = 0;

    timer(0, 10000).pipe(
      takeUntil(this.#stopPolling$),
      switchMap(() => this.getVideoStatus().pipe(
        catchError(() => {
          consecutiveErrors++;
          if (consecutiveErrors >= 3) {
            this.status$.next('error');
            this.#stopPolling$.next();
          }
          return EMPTY;
        })
      )),
      tap((response) => {
        consecutiveErrors = 0;
        attempts++;
        if (response.status === 'asset_created' || response.status === 'ready') {
          this.streamUrl$.next(response.video_url);
          this.status$.next('ready');
          this.#stopPolling$.next();
        } else if (response.status === 'errored' || attempts >= maxAttempts) {
          this.status$.next('error');
          this.#stopPolling$.next();
        }
      })
    ).subscribe();
  }

  private getVideoStatus(): Observable<MuxVideoStatusResponse> {
    return this.#http.get<MuxVideoStatusResponse>(
      this.#baseRequest.prepareUrl(['/api/videos', 'upload_status']),
      { params: { upload_id: this.#uploadId, video_id: this.#videoId.toString(), relation: 'video' } }
    );
  }
}
